/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import type { Product } from '@shared/types/products';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Search, Package, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';

interface ProductBrowserProps {
  products: Product[];
  selectedProduct: Product | null;
  onSelectProduct: (product: Product) => void;
}

export function ProductBrowser({ products, selectedProduct, onSelectProduct }: ProductBrowserProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    
    const searchLower = searchTerm.toLowerCase();
    return products.filter(product => 
      (product.productId ?? '').toLowerCase().includes(searchLower) ||
      product.title.toLowerCase().includes(searchLower) ||
      product.owner.toLowerCase().includes(searchLower)
    );
  }, [products, searchTerm]);

  return (
    <div className="flex flex-col h-full border-r border-border bg-muted/30">
      {/* Header */}
      <div className="p-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" />
          Products
        </h3>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>
      </div>

      {/* Product List */}
      <ScrollArea className="flex-1">
        <div className="py-1">
          {filteredProducts.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {searchTerm ? 'No products match your search.' : 'No products found.'}
            </div>
          ) : (
            filteredProducts.map(product => {
              const isSelected = selectedProduct?.id === product.id;
              return (
                <button
                  key={product.id}
                  onClick={() => onSelectProduct(product)}
                  className={`w-full px-3 py-2 text-left transition-colors flex items-center gap-2 group ${
                    isSelected 
                      ? 'bg-primary/10 border-l-2 border-primary' 
                      : 'hover:bg-muted border-l-2 border-transparent'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-mono ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                      {product.productId}
                    </div>
                    <div className={`text-sm font-medium truncate ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                      {product.title}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {product.owner}
                    </div>
                  </div>
                  <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-opacity ${
                    isSelected ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-50'
                  }`} />
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Footer showing count */}
      <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/50">
        {filteredProducts.length} of {products.length} products
      </div>
    </div>
  );
}

